<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcProductPrices',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    product: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const sortItems = (...args) => {
    return _.orderBy(args[0], ['valid_from', 'price'], ['desc', 'desc']);
  };

  const items = ref(null);
  const currencies = ref(['HUF', 'EUR', 'USD']);
  const ux = ref({
    initialLoadSpinner: true,
  });

  const $data = computed(() => ({
    items: items.value,
    currencies: currencies.value,
    ux: ux.value,
  }));

  const $$load_items = () => {
    return $fn.value.fetch('list_product_price', {
      parameters: { product_id: props.product.id },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'currencies' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: { initialLoadSpinner: true },
    dataRefs: { items, currencies, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const purchasePrices = computed(() => {
    if (loaded.value) {
      return sortItems(_.filter(items.value, { is_purchase_price: true }));
    }
    return null;
  });
  const sellingPrices = computed(() => {
    if (loaded.value) {
      return sortItems(_.filter(items.value, { is_purchase_price: false }));
    }
    return null;
  });

  watch(
    () => props.product,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('product');
    }
  );

  const inst_evh_8320016629450276_close_26r7i8pr6_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_8320016629450276_showModal_i81fzl9v0_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ProductPrice',
      props: {
        product: props.product,
        is_purchase_price: false,
      },
    };
  };
  const inst_evh_8855792642156263_close_mjli1iukz_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_8855792642156263_showModal_5j4c22t14_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ProductPrice',
      props: {
        oid: $event.scope.item.id,
        mode: 'edit',
        product: props.product,
      },
    };
  };
  const inst_evh_4694790035631985_close_bj1uqlip3_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_4694790035631985_showModal_57pif0m18_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ProductPrice',
      props: {
        product: props.product,
        is_purchase_price: true,
      },
    };
  };
  const inst_evh_7972369138378646_close_wd3czgsig_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_7972369138378646_showModal_p56bcx3x3_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ProductPrice',
      props: {
        oid: $event.scope.item.id,
        mode: 'edit',
        product: props.product,
      },
    };
  };

  const act_8320016629450276_reloadComponentData_tx1u86dxe =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8320016629450276_close_26r7i8pr6 =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_reloadComponentData_tx1u86dxe,
      event: 'close',
      when: inst_evh_8320016629450276_close_26r7i8pr6_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_8320016629450276_showModal_i81fzl9v0 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8320016629450276_showModal_i81fzl9v0_getActionArgs,
      events: [inst_evh_8320016629450276_close_26r7i8pr6],
    });
  const inst_evh_8320016629450276_clickPreventStop_dpzrwcnys =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_showModal_i81fzl9v0,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_dpzrwcnys],
      event: 'click.prevent.stop',
    });
  const act_8855792642156263_reloadComponentData_8duk62b2l =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8855792642156263_close_mjli1iukz =
    new actionTypes.EventHandlerImpl({
      action: act_8855792642156263_reloadComponentData_8duk62b2l,
      event: 'close',
      when: inst_evh_8855792642156263_close_mjli1iukz_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_8855792642156263_showModal_5j4c22t14 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8855792642156263_showModal_5j4c22t14_getActionArgs,
      events: [inst_evh_8855792642156263_close_mjli1iukz],
    });
  const inst_evh_8855792642156263_clickPreventStop_22gsf05do =
    new actionTypes.EventHandlerImpl({
      action: act_8855792642156263_showModal_5j4c22t14,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8855792642156263_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8855792642156263_clickPreventStop_22gsf05do],
      event: 'click.prevent.stop',
    });
  const act_4694790035631985_reloadComponentData_h6h3owfww =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_4694790035631985_close_bj1uqlip3 =
    new actionTypes.EventHandlerImpl({
      action: act_4694790035631985_reloadComponentData_h6h3owfww,
      event: 'close',
      when: inst_evh_4694790035631985_close_bj1uqlip3_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_4694790035631985_showModal_57pif0m18 =
    new actionTypes.ShowModalAction({
      actionArgs: act_4694790035631985_showModal_57pif0m18_getActionArgs,
      events: [inst_evh_4694790035631985_close_bj1uqlip3],
    });
  const inst_evh_4694790035631985_clickPreventStop_6hv06m85y =
    new actionTypes.EventHandlerImpl({
      action: act_4694790035631985_showModal_57pif0m18,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_4694790035631985_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4694790035631985_clickPreventStop_6hv06m85y],
      event: 'click.prevent.stop',
    });
  const act_7972369138378646_reloadComponentData_r718lklrm =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7972369138378646_close_wd3czgsig =
    new actionTypes.EventHandlerImpl({
      action: act_7972369138378646_reloadComponentData_r718lklrm,
      event: 'close',
      when: inst_evh_7972369138378646_close_wd3czgsig_getWhen,
      displayName: 'reloadComponentData',
    });
  const act_7972369138378646_showModal_p56bcx3x3 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7972369138378646_showModal_p56bcx3x3_getActionArgs,
      events: [inst_evh_7972369138378646_close_wd3czgsig],
    });
  const inst_evh_7972369138378646_clickPreventStop_yvw3xs33t =
    new actionTypes.EventHandlerImpl({
      action: act_7972369138378646_showModal_p56bcx3x3,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_7972369138378646_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7972369138378646_clickPreventStop_yvw3xs33t],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_8320016629450276_close_26r7i8pr6,
    inst_evh_8320016629450276_clickPreventStop_dpzrwcnys,
    inst_evh_8320016629450276_clickPreventStop,
    inst_evh_8855792642156263_close_mjli1iukz,
    inst_evh_8855792642156263_clickPreventStop_22gsf05do,
    inst_evh_8855792642156263_clickPreventStop,
    inst_evh_4694790035631985_close_bj1uqlip3,
    inst_evh_4694790035631985_clickPreventStop_6hv06m85y,
    inst_evh_4694790035631985_clickPreventStop,
    inst_evh_7972369138378646_close_wd3czgsig,
    inst_evh_7972369138378646_clickPreventStop_yvw3xs33t,
    inst_evh_7972369138378646_clickPreventStop,
  };

  const evh_8320016629450276_close_26r7i8pr6 = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_close_26r7i8pr6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_dpzrwcnys = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_dpzrwcnys.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8855792642156263_close_mjli1iukz = async (event, scope) => {
    return await actions.inst_evh_8855792642156263_close_mjli1iukz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8855792642156263_clickPreventStop_22gsf05do = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8855792642156263_clickPreventStop_22gsf05do.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8855792642156263_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8855792642156263_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4694790035631985_close_bj1uqlip3 = async (event, scope) => {
    return await actions.inst_evh_4694790035631985_close_bj1uqlip3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4694790035631985_clickPreventStop_6hv06m85y = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4694790035631985_clickPreventStop_6hv06m85y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4694790035631985_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4694790035631985_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7972369138378646_close_wd3czgsig = async (event, scope) => {
    return await actions.inst_evh_7972369138378646_close_wd3czgsig.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7972369138378646_clickPreventStop_yvw3xs33t = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7972369138378646_clickPreventStop_yvw3xs33t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7972369138378646_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7972369138378646_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpWidgetCard class="mb-3">
    <template #header>
      <ItpBox alignItems="center">
        <strong>
          {{ pgettext('ProductPrices', 'Selling prices') }}
        </strong>
        <ItpButton
          :text="pgettext('ProductPrices', 'Add price...')"
          variant="light"
          size="sm"
          class="ms-auto"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </template>
    <BListGroup v-if="loaded">
      <BListGroupItem v-if="!sellingPrices.length">
        <span>
          {{ pgettext('ProductPrices', 'No prices.') }}
        </span>
      </BListGroupItem>
      <BListGroupItem
        :key="item.id"
        class="d-flex flex-column"
        v-for="item in sellingPrices"
      >
        <ItpBox alignItems="start">
          <ItpIcon
            :icon="
              item.is_purchase_price
                ? 'icons8:buy'
                : 'material-symbols:sell-outline'
            "
          >
          </ItpIcon>
          <ItpBox direction="column" class="w-100">
            <ItpBox class="W-100">
              <ItpBox class="col-md-3">
                <ItpText :strikethrough="item.inactive" class="fw-bold me-3">
                  {{ number(item.price) }} {{ item.currency }}
                </ItpText>
                <UcTariffCodeBadge
                  :value="item.tariff_code"
                  class="me-3"
                  v-if="item.tariff_code"
                >
                </UcTariffCodeBadge>
                <ItpBadge
                  :title="pgettext('ProductPrice', 'From quantity')"
                  no-box-shadow
                  variant="light"
                  class="me-3 badge-icon border"
                  v-if="item.quantity_from"
                >
                  <ItpIcon name="fa-solid:greater-than-equal"> </ItpIcon>
                  <ItpText :text="item.quantity_from" class="ms-2"> </ItpText>
                  <ItpText :text="product.unit" class="ms-1"> </ItpText>
                </ItpBadge>
                <ItpBadge
                  :title="pgettext('ProductPrice', 'Discount price')"
                  no-box-shadow
                  variant="warning"
                  class="me-3 badge-icon boredr"
                  v-if="item.is_discount_price"
                >
                  <ItpIcon name="mdi:discount-outline"> </ItpIcon>
                </ItpBadge>
              </ItpBox>
              <ItpBox class="col-md-3">
                <UcValidityPeriod
                  :from="item.valid_from"
                  :to="item.valid_to"
                  class="ps-3"
                >
                </UcValidityPeriod>
              </ItpBox>
              <ItpBox alignItems="start" wrap-items class="col-md-6">
                <UcCompactCompanyDisplay
                  :payload="item.partner"
                  class="ps-3"
                  v-if="item.partner"
                >
                </UcCompactCompanyDisplay>
              </ItpBox>
            </ItpBox>
            <ItpBox class="W-100">
              <div class="col-md-3">
                <ItpText :text="item.note" pre v-if="item.note"> </ItpText>
              </div>
            </ItpBox>
          </ItpBox>
          <ItpButton
            icon="fa-pen"
            :tooltip="gettext('Edit')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_8855792642156263_clickPreventStop($event, { item })
            "
          >
          </ItpButton>
        </ItpBox>
      </BListGroupItem>
    </BListGroup>
  </ItpWidgetCard>
  <ItpWidgetCard>
    <template #header>
      <ItpBox alignItems="center">
        <strong>
          {{ pgettext('ProductPrices', 'Purchase prices') }}
        </strong>
        <ItpButton
          :text="pgettext('ProductPrices', 'Add price...')"
          variant="light"
          size="sm"
          class="ms-auto"
          @click.prevent.stop="
            evh_4694790035631985_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </template>
    <BListGroup v-if="loaded">
      <BListGroupItem v-if="!purchasePrices.length">
        <span>
          {{ pgettext('ProductPrices', 'No prices.') }}
        </span>
      </BListGroupItem>
      <BListGroupItem
        :key="item.id"
        class="d-flex flex-column"
        v-for="item in purchasePrices"
      >
        <ItpBox alignItems="start">
          <ItpIcon
            :icon="
              item.is_purchase_price
                ? 'icons8:buy'
                : 'material-symbols:sell-outline'
            "
          >
          </ItpIcon>
          <ItpBox direction="column" class="w-100">
            <ItpBox class="W-100">
              <ItpBox class="col-md-3">
                <ItpText :strikethrough="item.inactive" class="fw-bold me-3">
                  {{ number(item.price) }} {{ item.currency }}
                </ItpText>
                <UcTariffCodeBadge
                  :value="item.tariff_code"
                  class="me-3"
                  v-if="item.tariff_code"
                >
                </UcTariffCodeBadge>
                <ItpBadge
                  :title="pgettext('ProductPrice', 'From quantity')"
                  no-box-shadow
                  variant="light"
                  class="me-3 badge-icon border"
                  v-if="item.quantity_from"
                >
                  <ItpIcon name="fa-solid:greater-than-equal"> </ItpIcon>
                  <ItpText :text="item.quantity_from" class="ms-2"> </ItpText>
                  <ItpText :text="product.unit" class="ms-1"> </ItpText>
                </ItpBadge>
                <ItpBadge
                  :title="pgettext('ProductPrice', 'Discount price')"
                  no-box-shadow
                  variant="warning"
                  class="me-3 badge-icon boredr"
                  v-if="item.is_discount_price"
                >
                  <ItpIcon name="mdi:discount-outline"> </ItpIcon>
                </ItpBadge>
              </ItpBox>
              <ItpBox class="col-md-3">
                <UcValidityPeriod
                  :from="item.valid_from"
                  :to="item.valid_to"
                  class="ps-3"
                >
                </UcValidityPeriod>
              </ItpBox>
              <ItpBox alignItems="start" wrap-items class="col-md-6">
                <UcCompactCompanyDisplay
                  :payload="item.partner"
                  class="ps-3"
                  v-if="item.partner"
                >
                </UcCompactCompanyDisplay>
              </ItpBox>
            </ItpBox>
            <ItpBox class="W-100">
              <div class="col-md-3">
                <ItpText :text="item.note" pre v-if="item.note"> </ItpText>
              </div>
            </ItpBox>
          </ItpBox>
          <ItpButton
            icon="fa-pen"
            :tooltip="gettext('Edit')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_7972369138378646_clickPreventStop($event, { item })
            "
          >
          </ItpButton>
        </ItpBox>
      </BListGroupItem>
    </BListGroup>
  </ItpWidgetCard>
</template>
